
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { onAuthStateChange, verifyTokenWithBackend, checkEmailVerificationStatus } from '../lib/firebase';
import { EmailVerificationGate } from './EmailVerificationGate';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isEmailVerified: boolean;
  refreshUser: (email?: string) => Promise<boolean>;
  setVerificationModalOpen: (isOpen: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isEmailVerified: false,
  refreshUser: async () => false,
  setVerificationModalOpen: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);

  const setVerificationModalOpen = (isOpen: boolean) => {
    setIsVerificationModalOpen(isOpen);
  };

  const refreshUser = async (email?: string): Promise<boolean> => {
    try {
      // Use the dedicated function to check email verification
      const isVerified = await checkEmailVerificationStatus(email);
      
      // Update local state
      const wasVerified = isEmailVerified;
      setIsEmailVerified(isVerified);
      
      return isVerified;
    } catch (error) {
      return false;
    }
  };

  // Polling effect to check email verification status
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (user && !isEmailVerified) {
      // Check if user is on verification page or modal is open for more aggressive polling
      const isOnVerificationPage = window.location.pathname === '/verify-email' || 
                                   window.location.search.includes('mode=verifyEmail');
      
      // More frequent polling when verification modal is open or on verification page
      const pollInterval = (isOnVerificationPage || isVerificationModalOpen) ? 2000 : 5000; // 2s when focused on verification, 5s elsewhere
      
      // Start polling when user is logged in but not verified
      intervalId = setInterval(async () => {
        try {
          await user.reload();
          const wasVerified = isEmailVerified;
          const nowVerified = user.emailVerified;
          
          if (nowVerified !== wasVerified) {
            setIsEmailVerified(nowVerified);
            if (nowVerified) {
              // If email was just verified, also refresh the token
              const idToken = await user.getIdToken(true);
              await verifyTokenWithBackend(idToken);
            }
          }
        } catch (error) {
          // Silent error handling for polling
        }
      }, pollInterval);
    } else if (user) {
      // User is already verified, no polling needed
    } else {
      // No user logged in, no polling needed
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user, isEmailVerified, isVerificationModalOpen]);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (user) => {
      if (user) {
        try {
          // Get fresh user data to check email verification status
          await user.reload();
          // Get fresh token to ensure updated claims
          const idToken = await user.getIdToken(true); // true forces refresh
          await verifyTokenWithBackend(idToken);
          setUser(user);
          const emailVerified = user.emailVerified;
          setIsEmailVerified(emailVerified);
        } catch (error) {
          setUser(null);
          setIsEmailVerified(false);
        }
      } else {
        setUser(null);
        setIsEmailVerified(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    loading,
    isEmailVerified,
    refreshUser,
    setVerificationModalOpen,
  };

  return (
    <AuthContext.Provider value={value}>
      <EmailVerificationGate>
        {children}
      </EmailVerificationGate>
    </AuthContext.Provider>
  );
};
